import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  HistoryEntry,
  HistoryPersistenceState,
  PAGE_HISTORY_MAX_ENTRIES,
  PAGE_HISTORY_STORAGE_KEY,
} from './page-history.model';

/**
 * Tracks the history of pages and items the user has interacted with.
 *
 * Every time a tab is opened or re-visited, an entry is recorded (or bumped)
 * and the full history is persisted to the browser's localStorage so it
 * survives reloads and sessions. The newest interaction is always first.
 */
@Injectable({ providedIn: 'root' })
export class PageHistoryService {
  private readonly entriesSubject = new BehaviorSubject<HistoryEntry[]>([]);

  /** Observable stream of history entries (most recent first) */
  readonly history$ = this.entriesSubject.asObservable();

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Record an interaction with a page/item. If an entry for the same route
   * already exists it is moved to the front, its visit count is incremented
   * and its params are merged (so a display name that loads later is captured).
   */
  record(entry: Omit<HistoryEntry, 'openedAt' | 'visitCount'>): void {
    const now = Date.now();
    const current = this.entriesSubject.value;
    const existing = current.find((e) => e.id === entry.id);

    const updated: HistoryEntry = {
      ...entry,
      params: { ...(existing?.params || {}), ...(entry.params || {}) },
      openedAt: now,
      visitCount: (existing?.visitCount || 0) + 1,
    };

    // Drop the previous occurrence, prepend the updated one, then cap the list.
    const next = [updated, ...current.filter((e) => e.id !== entry.id)].slice(
      0,
      PAGE_HISTORY_MAX_ENTRIES
    );

    this.emit(next);
  }

  /**
   * Update the display name of an existing entry (e.g. once an item's record
   * has loaded and a human-readable label becomes available).
   */
  updateDisplayName(id: string, displayName: string): void {
    if (!displayName) return;
    const current = this.entriesSubject.value;
    const entry = current.find((e) => e.id === id);
    if (!entry) return;

    const next = current.map((e) =>
      e.id === id ? { ...e, params: { ...(e.params || {}), displayName } } : e
    );
    this.emit(next);
  }

  /** Get a snapshot of the current history (most recent first) */
  getHistory(): HistoryEntry[] {
    return this.entriesSubject.value;
  }

  /** Remove a single entry from the history */
  remove(id: string): void {
    this.emit(this.entriesSubject.value.filter((e) => e.id !== id));
  }

  /** Clear the entire history */
  clear(): void {
    this.emit([]);
  }

  /** Publish the new list and persist it */
  private emit(entries: HistoryEntry[]): void {
    this.entriesSubject.next(entries);
    this.saveToStorage(entries);
  }

  private saveToStorage(entries: HistoryEntry[]): void {
    try {
      const state: HistoryPersistenceState = { entries, savedAt: Date.now() };
      localStorage.setItem(PAGE_HISTORY_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('[PageHistoryService] Failed to persist history:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(PAGE_HISTORY_STORAGE_KEY);
      if (!stored) return;
      const state: HistoryPersistenceState = JSON.parse(stored);
      if (Array.isArray(state.entries)) {
        this.entriesSubject.next(state.entries.slice(0, PAGE_HISTORY_MAX_ENTRIES));
      }
    } catch (error) {
      console.warn('[PageHistoryService] Failed to load history:', error);
      this.entriesSubject.next([]);
    }
  }
}
